import { useTranslation } from 'react-i18next';
import { ShoppingBag } from 'lucide-react';

import { Breadcrumb, EmptyState } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';

export interface StubPageProps {
  /** i18n key for the page title (resolved against `common` namespace). */
  titleKey: string;
  /** Feature slug for the `data-page` attribute required by TDD §3.14. */
  dataPage: string;
}

/**
 * Placeholder page used by routes whose features haven't been built yet.
 * Keeps the shell navigable and the 403/role tests meaningful without
 * committing to real screen designs too early.
 */
export function StubPage({ titleKey, dataPage }: StubPageProps) {
  const { t } = useTranslation('common');
  const title = t(titleKey as Parameters<typeof t>[0]);

  const isOrders = dataPage === 'orders/index';
  return (
    <div data-page={dataPage} className="mx-auto flex min-h-full w-full max-w-6xl flex-col">
      <Breadcrumb
        className="mb-4"
        items={[
          { label: t('nav.home' as Parameters<typeof t>[0]), to: '/home' },
          { label: title, current: true },
        ]}
      />
      <header className="mb-6 border-b border-border pb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-fg">{title}</h1>
            <p className="mt-1 text-sm text-fg-muted">
              {isOrders
                ? 'Track, triage, and manage orders across every store.'
                : 'Coming in a future sprint.'}
            </p>
          </div>
          <div className={cn('min-h-9', 'flex items-center justify-end')} />
        </div>
      </header>

      {isOrders ? (
        <EmptyState
          icon={ShoppingBag}
          title="No orders yet"
          description="Orders will appear here once the backend is connected."
        />
      ) : (
        <div className="text-sm text-fg-muted">Coming in a future sprint.</div>
      )}
    </div>
  );
}
